/**
 * Teacher-feedback user role document refs for the client SDK.
 * Path: teacherFeedback/auth/users/{uid}/roles/{courseId}
 *
 * Used by the teacher-feedback web app to read role documents via
 * snapshot listeners (e.g. in AuthContext).
 */
import type {
  CollectionReference,
  DocumentReference,
  Firestore,
} from 'firebase/firestore';
import {collection, doc, getFirestore} from 'firebase/firestore';

import {
  TeacherFeedbackUserRoleSchema,
  getTeacherFeedbackRoleDocumentPath,
  getTeacherFeedbackRolesCollectionPath,
  type TeacherFeedbackUserRole,
} from '../../documents/teacherFeedback/userRole';
import {createConverter} from '../sdk';

// ============================================================================
// Converter
// ============================================================================

const teacherFeedbackUserRoleConverter = createConverter({
  schema: TeacherFeedbackUserRoleSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

// ============================================================================
// Document Refs
// ============================================================================

/**
 * Returns a typed document ref for a user's role for a specific course.
 * Path: teacherFeedback/auth/users/{uid}/roles/{courseId}
 */
export function getTeacherFeedbackRoleDocRef({
  uid,
  courseId,
  firestore = getFirestore(),
}: {
  uid: string;
  courseId: string;
  firestore?: Firestore;
}): DocumentReference<TeacherFeedbackUserRole> {
  return doc(
    firestore,
    getTeacherFeedbackRoleDocumentPath({uid, courseId}),
  ).withConverter(teacherFeedbackUserRoleConverter);
}

/**
 * Returns a typed collection ref for all of a user's roles across courses.
 * Path: teacherFeedback/auth/users/{uid}/roles
 */
export function getTeacherFeedbackRolesCollectionRef({
  uid,
  firestore = getFirestore(),
}: {
  uid: string;
  firestore?: Firestore;
}): CollectionReference<TeacherFeedbackUserRole> {
  return collection(
    firestore,
    getTeacherFeedbackRolesCollectionPath({uid}),
  ).withConverter(teacherFeedbackUserRoleConverter);
}
